import {
  AbstractControl, FormControl, ValidatorFn, Validators,
} from '@angular/forms';
import { BaseForm } from './base.form';
import { LockboxForm } from './lockbox.form';
import { PhotographerAppointment, Lockbox } from '../models/photographer-appointment.model';
import { PhotographyAgencyForm } from './photography-agency.form';
import { CustomValidator } from '../validators';
import { CatalogPhotographyVendor } from '../models/catalog-photography-vendor.model';
import { PhotographySource } from '../models/photography-source';
import { PhotographyProvider } from '../models/photography-provider.model';

export class PhotographerAppointmentForm extends BaseForm<PhotographerAppointment> {
  readonly catalogVendor: FormControl<CatalogPhotographyVendor | null>;
  get provider(): PhotographyProvider { return PhotographyProvider.forAppointment(this.value); }
  get providerControl(): AbstractControl { return this.catalogVendor.enabled ? this.catalogVendor : this.photoAgency; }
  // accessors are not required, but reduce code and risk of not retrieving the proper control
  public get comments(): string { return this.get('comments').value; }
  public get lockboxAvailable(): boolean { return this.get('lockboxAvailable').value; }
  public get lockbox(): Lockbox { return this.get('lockbox').value; }
  public get photoAgency(): PhotographyAgencyForm { return this.getControl('photoAgency') as PhotographyAgencyForm; }

  constructor(value?: PhotographerAppointment, required?: boolean) {
    const catalogVendor = new FormControl<CatalogPhotographyVendor | null>(PhotographyProvider.forAppointment(value)?.catalogSnapshot ?? null);
    super({
      catalogVendor,
      lockboxAvailable: new FormControl(false),
      comments: new FormControl(null),
      lockbox: new LockboxForm(value ? value.lockbox : null),
      photoAgency: new PhotographyAgencyForm(value ? value.photoAgency : null),
      scheduledAt: new FormControl(value?.scheduledAt),
      timezone: new FormControl(value?.timezone),
      timezoneOffset: new FormControl(value?.timezoneOffset),
    });
    this.catalogVendor = catalogVendor;
    this.configureSource(catalogVendor.value ? PhotographySource.Catalog : PhotographySource.Legacy);

    if (required) {
      // Only require the following fields if useCustomScheduling is false
      const requiredValidator: ValidatorFn = (control) => (this.provider?.useCustomScheduling ? null : CustomValidator.required(control));
      this.getControl('scheduledAt').setValidators([requiredValidator]);
      this.getControl('timezone').setValidators([requiredValidator]);
      this.getControl('timezoneOffset').setValidators([requiredValidator]);
      this.updateSchedulingValidation();
    }

    if (value) {
      const model = { ...value };
      delete model.lockbox;
      this.patchValue(model);
    }
  }

  configureSource(source: PhotographySource): void {
    if (source === PhotographySource.Catalog) {
      this.photoAgency.disable({ emitEvent: false });
      this.catalogVendor.enable({ emitEvent: false });
      this.catalogVendor.setValidators([
        Validators.required,
        (control) => (control.value && !control.value.email ? { contactEmailRequired: true } : null),
        (control) => (control.value?.useCustomScheduling && !control.value.customSchedulingUrl ? { schedulingUrlRequired: true } : null),
      ]);
    } else {
      this.photoAgency.enable({ emitEvent: false });
      this.catalogVendor.clearValidators();
      this.catalogVendor.disable({ emitEvent: false });
    }
    this.catalogVendor.updateValueAndValidity({ emitEvent: false });
    this.updateSchedulingValidation();
  }

  override getValue(options?: { removeNulls?: boolean; onlyDirty?: boolean }) {
    const value = super.getValue(options);
    if (this.catalogVendor.enabled) delete value.photoAgency;
    else delete value.catalogVendor;
    return value;
  }

  selectProvider(provider?: PhotographyProvider): void {
    if (this.catalogVendor.enabled) {
      this.catalogVendor.setValue(provider?.catalogSnapshot ?? null);
      this.catalogVendor.markAsDirty();
      this.photoAgency.reset();
    } else {
      this.photoAgency.reset();
      if (provider?.agency) this.photoAgency.patchValue(provider.agency);
      this.photoAgency.markAsDirty();
    }
    this.updateSchedulingValidation();
  }

  private updateSchedulingValidation(): void {
    for (const field of ['scheduledAt', 'timezone', 'timezoneOffset']) {
      this.get(field).updateValueAndValidity({ emitEvent: false });
    }
  }
}
